import React, { useEffect, useState } from 'react';
import {
  TableRow,
  TableCell,
  Paper,
  Typography,
  Grid,
  InputAdornment,
  TextField,
  Box,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Table from '../../../components/Table';

import { PersonType } from '../../../schema/person';
import MultiSelect from '../../../components/MultiSelect';
import { TableResponse } from '../../../schema/helper';
import { api } from '../../../api';

interface NumberInOut {
  person_id: number;
  company: string;
  personnal_id: string;
  name: string;
  person_type: string;
  supervisor: string;
  area: string;
  status_id: number;
  status: string;
}

const tableHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'ประเภท',
  'บริษัท',
  'ผู้คุมงาน',
  'ประตู',
  'สถานะ'
];
const InProcessAreaTable: React.FC = () => {
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);

  const [data, setData] = useState<TableResponse<NumberInOut> | null>(null);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', `${filterID}`);
    }
    if (filterName) {
      searchParams.set('name', `${filterName}`);
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    setIsExportLoading(true);
    api
      .get(`/inplant-person/export`, { params: searchParams, responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'inplant-person_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      const searchParams = new URLSearchParams();
      if (filterID) {
        searchParams.set('personal_id', `${filterID}`);
      }
      if (filterName) {
        searchParams.set('name', `${filterName}`);
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }
      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);

      setIsLoading(true);
      api
        .get<TableResponse<NumberInOut>>(`/inplant-person`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setData(data);
          setIsLoading(false);
        });
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [filterID, filterName, filterPersonTypeIDList, page, rowsPerPage]);

  useEffect(() => {
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);

  return (
    <Paper>
      <Box display="flex" justifyContent="space-between" sx={{ p: 2, pb: 0 }}>
        <Typography variant="h6" display="inline" sx={{ py: 1 }}>
          บุคคลที่อยู่ในพื้นที่
        </Typography>
        <Button variant="outlined" onClick={handleExportCSVFile} disabled={isExportLoading}>
          Export
        </Button>
      </Box>
      {/* <Typography variant="h6" sx={{ p: 2 }}>
        บุคคลที่อยู่ในพื้นที่
      </Typography> */}
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item md={4}>
          <TextField
            id="search-text-label"
            label="ค้นหาเลขบัตรประชาชน"
            variant="outlined"
            placeholder="ค้นหาเลขบัตรประชาชน"
            onChange={(event) => setFilterID(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            id="search-text-label"
            label="ค้นหาชื่อ - นามสกุล"
            variant="outlined"
            placeholder="ค้นหาชื่อ - นามสกุล"
            onChange={(event) => setFilterName(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete={'off'}
          />
        </Grid>
        <Grid item md={4}>
          <MultiSelect
            options={personTypes.map((data) => ({ id: data.id.toString(), name: data.type_name }))}
            onChange={(data) => setFilterPersonTypeIDList(data)}
            label="ประเภทบุคคล"
          />
        </Grid>
      </Grid>
      <Table
        heads={tableHead}
        count={data?.total ? data.total : 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
      >
        {data?.data.map((data, index) => (
          <TableRow key={index}>
            <TableCell align="center">{data.personnal_id}</TableCell>
            <TableCell align="center">{data.name}</TableCell>
            <TableCell align="center">{data.person_type}</TableCell>
            <TableCell align="center">{data.company}</TableCell>
            <TableCell align="center">{data.supervisor}</TableCell>
            <TableCell align="center">{data.area}</TableCell>
            <TableCell align="center">
              <Typography variant="body2" color={(data.status == "ยังไม่ได้แลกบัตรล่วงเวลา" || data.status == "ไม่พบข้อมูลการแลกบัตร") ? 'error' : 'success.main'}>
                {data.status}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </Table>
    </Paper>
  );
};

export default InProcessAreaTable;
